import React, { useState } from "react";
import { API_BASE } from "../api/config";
import LocationSelect from "../components/LocationSelect";
import "./ManualStockMovementForm.css";

function todayKey() {
  const d = new Date();
  d.setMinutes(d.getMinutes() - d.getTimezoneOffset());
  return d.toISOString().slice(0, 10);
}

const MOVEMENT_TYPES = [
  { value: "IMPORT_RECEIVE", label: "Import Receive / Stock In" },
  { value: "TRANSFER", label: "Transfer Location to Location" },
  { value: "SALE_RETURN", label: "Sale Return / Stock In" },
  { value: "ADJUSTMENT_IN", label: "Adjustment In" },
  { value: "ADJUSTMENT_OUT", label: "Adjustment Out" },
  { value: "DAMAGE", label: "Damage" },
  { value: "LOST", label: "Lost" },
];

const EMPTY_FORM = {
  movementDate: todayKey(),
  movementType: "IMPORT_RECEIVE",
  productCode: "",
  color: "",
  size: "",
  brand: "PRETTYFIT",
  locationCode: "DNGPF",
  fromLocationCode: "DNGPF",
  toLocationCode: "MDYHT",
  qty: "",
  unitCostMmk: "",
  batchNo: "",
  voucherNo: "",
  note: "",
};

async function postStockMovement(payload) {
  const res = await fetch(`${API_BASE}/api/erp/stock-movement`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
    },
    body: JSON.stringify(payload),
  });

  const data = await res.json();

  if (!res.ok || data.success === false) {
    throw new Error(data.message || "Failed to save stock movement");
  }

  return data;
}

export default function ManualStockMovementForm({ onSaved }) {
  const [form, setForm] = useState(EMPTY_FORM);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState("");
  const [error, setError] = useState("");

  const isTransfer = form.movementType === "TRANSFER";

  function update(name, value) {
    setForm((prev) => ({
      ...prev,
      [name]: value,
    }));
  }

  function resetForm() {
    setForm({
      ...EMPTY_FORM,
      movementDate: todayKey(),
    });
    setMessage("");
    setError("");
  }

  async function submitMovement(e) {
    e.preventDefault();

    try {
      setSaving(true);
      setError("");
      setMessage("");

      const productCode = String(form.productCode || "").trim().toUpperCase();
      const qty = Number(form.qty || 0);
      const unitCostMmk = Number(form.unitCostMmk || 0);

      if (!productCode) {
        throw new Error("Product code ထည့်ပါ");
      }

      if (qty <= 0) {
        throw new Error("Qty ကို 0 ထက်ကြီးအောင်ထည့်ပါ");
      }

      const basePayload = {
        movementDate: form.movementDate,
        productCode,
        color: form.color,
        size: form.size,
        brand: form.brand,
        qty,
        unitCostMmk,
        batchNo: form.batchNo,
        voucherNo: form.voucherNo,
        note: form.note,
        createdBy: "ADMIN",
      };

      if (isTransfer) {
        if (!form.fromLocationCode || !form.toLocationCode) {
          throw new Error("From location / To location ရွေးပါ");
        }

        if (form.fromLocationCode === form.toLocationCode) {
          throw new Error("From location နဲ့ To location တူလို့မရပါ");
        }

        await postStockMovement({
          ...basePayload,
          locationCode: form.fromLocationCode,
          movementType: "TRANSFER_OUT",
          fromLocationCode: form.fromLocationCode,
          toLocationCode: form.toLocationCode,
          note: form.note || `Transfer out to ${form.toLocationCode}`,
        });

        await postStockMovement({
          ...basePayload,
          locationCode: form.toLocationCode,
          movementType: "TRANSFER_IN",
          fromLocationCode: form.fromLocationCode,
          toLocationCode: form.toLocationCode,
          note: form.note || `Transfer in from ${form.fromLocationCode}`,
        });

        setMessage("Transfer stock movement saved ✅");
      } else {
        await postStockMovement({
          ...basePayload,
          locationCode: form.locationCode,
          movementType: form.movementType,
        });

        setMessage("Stock movement saved ✅");
      }

      onSaved?.();
    } catch (err) {
      setError(err.message || "Save failed");
    } finally {
      setSaving(false);
    }
  }

  return (
    <div className="manualStock">
      <div className="manualStock__head">
        <div>
          <span>Manual Movement</span>
          <h2>Manual Stock Movement Form</h2>
          <p>
            Stock ဝင်/ထွက်, transfer, adjustment, damage, lost များကို movement ledger အဖြစ်သိမ်းမယ်။
          </p>
        </div>

        <button type="button" onClick={resetForm}>
          Clear
        </button>
      </div>

      {message && <div className="manualStockMsg manualStockMsg--ok">{message}</div>}
      {error && <div className="manualStockMsg manualStockMsg--error">{error}</div>}

      <form className="manualStockForm" onSubmit={submitMovement}>
        <div className="manualStockGrid">
          <label>
            <span>Date</span>
            <input
              type="date"
              value={form.movementDate}
              onChange={(e) => update("movementDate", e.target.value)}
            />
          </label>

          <label>
            <span>Movement Type</span>
            <select
              value={form.movementType}
              onChange={(e) => update("movementType", e.target.value)}
            >
              {MOVEMENT_TYPES.map((item) => (
                <option key={item.value} value={item.value}>
                  {item.label}
                </option>
              ))}
            </select>
          </label>

          {!isTransfer && (
            <label>
              <span>Location</span>
              <LocationSelect
                value={form.locationCode}
                onChange={(value) => update("locationCode", value)}
                mode="all"
                includeAll={false}
              />
            </label>
          )}

          {isTransfer && (
            <>
              <label>
                <span>From Location</span>
                <LocationSelect
                  value={form.fromLocationCode}
                  onChange={(value) => update("fromLocationCode", value)}
                  mode="all"
                  includeAll={false}
                />
              </label>

              <label>
                <span>To Location</span>
                <LocationSelect
                  value={form.toLocationCode}
                  onChange={(value) => update("toLocationCode", value)}
                  mode="all"
                  includeAll={false}
                />
              </label>
            </>
          )}

          <label>
            <span>Product Code</span>
            <input
              value={form.productCode}
              onChange={(e) => update("productCode", e.target.value)}
              placeholder="3423H"
            />
          </label>

          <label>
            <span>Color</span>
            <input
              value={form.color}
              onChange={(e) => update("color", e.target.value)}
              placeholder="Ivory"
            />
          </label>

          <label>
            <span>Size</span>
            <input
              value={form.size}
              onChange={(e) => update("size", e.target.value)}
              placeholder="36"
            />
          </label>

          <label>
            <span>Brand</span>
            <select value={form.brand} onChange={(e) => update("brand", e.target.value)}>
              <option value="PRETTYFIT">PRETTYFIT</option>
              <option value="HANGTEN">HANGTEN</option>
            </select>
          </label>

          <label>
            <span>Qty</span>
            <input
              type="number"
              min="0"
              step="1"
              value={form.qty}
              onChange={(e) => update("qty", e.target.value)}
              placeholder="10"
            />
          </label>

          <label>
            <span>Unit Cost MMK</span>
            <input
              type="number"
              min="0"
              step="1"
              value={form.unitCostMmk}
              onChange={(e) => update("unitCostMmk", e.target.value)}
              placeholder="79375"
            />
          </label>

          <label>
            <span>Batch No</span>
            <input
              value={form.batchNo}
              onChange={(e) => update("batchNo", e.target.value)}
              placeholder="DEMO-HT-001"
            />
          </label>

          <label>
            <span>Voucher No</span>
            <input
              value={form.voucherNo}
              onChange={(e) => update("voucherNo", e.target.value)}
              placeholder="optional"
            />
          </label>
        </div>

        <label className="manualStockNote">
          <span>Note</span>
          <textarea
            value={form.note}
            onChange={(e) => update("note", e.target.value)}
            placeholder="Reason / remark..."
          />
        </label>

        <div className="manualStockActions">
          <div>
            {isTransfer ? (
              <b>Transfer mode: OUT + IN movement ၂ ကြောင်း auto သိမ်းမယ်</b>
            ) : (
              <b>Single movement mode</b>
            )}
          </div>

          <button type="submit" disabled={saving}>
            {saving ? "Saving..." : "Save Stock Movement"}
          </button>
        </div>
      </form>
    </div>
  );
}
